import type { LabSourceEngine } from '../types';
import type { SourceFile } from './engine-detection';

const COMMON = new Set(['.ds_store', '.git', '.idea', '.vs', 'node_modules']);
const ENGINE_EXCLUSIONS: Record<Exclude<LabSourceEngine, 'unknown'>, Set<string>> = {
  godot: new Set(['.godot', '.import']),
  unity: new Set(['library', 'temp', 'logs', 'obj', 'build', 'builds', '.vs']),
  unreal: new Set(['binaries', 'deriveddatacache', 'intermediate', 'saved']),
};

function relativeToProject(path: string, projectRoot: string): string {
  if (projectRoot === '.') return path;
  const prefix = `${projectRoot}/`;
  if (path === projectRoot) return '';
  if (!path.startsWith(prefix)) return path;
  return path.slice(prefix.length);
}

function generatedAtProjectRoot(engine: Exclude<LabSourceEngine, 'unknown'>, relativePath: string): string | null {
  const segments = relativePath.split('/').filter((segment) => segment.length > 0);
  if (segments.length === 0) return null;
  const first = segments[0].toLowerCase();
  if (ENGINE_EXCLUSIONS[engine].has(first)) return segments[0];
  if (engine !== 'unreal' || first !== 'plugins' || segments.length < 3) return null;
  const pluginFolder = segments[2].toLowerCase();
  if (pluginFolder !== 'binaries' && pluginFolder !== 'intermediate') return null;
  return segments[2];
}

export function applyEngineExclusions(
  files: SourceFile[],
  engine: Exclude<LabSourceEngine, 'unknown'>,
  additionalSegments: string[] = [],
  projectRoot = '.',
): { included: SourceFile[]; excludedPaths: string[] } {
  const additional = new Set(additionalSegments.map((segment) => segment.trim().toLowerCase()).filter(Boolean));
  const excludedPaths = new Set<string>();
  const included = files.filter(({ path }) => {
    const relative = relativeToProject(path, projectRoot);
    const segments = relative.split('/').filter((segment) => segment.length > 0);
    const generated = generatedAtProjectRoot(engine, relative);
    const anywhere = segments.find((segment) => {
      const normalized = segment.toLowerCase();
      return COMMON.has(normalized) || additional.has(normalized);
    });
    const excluded = generated ?? anywhere;
    if (!excluded) return true;
    excludedPaths.add(excluded);
    return false;
  });
  return { included, excludedPaths: [...excludedPaths].sort() };
}
